import React, {
  useState,
  useEffect,
  useCallback,
  Dispatch,
  SetStateAction,
} from "react";
import moment from "moment";

import { ModalWrapper, TableWrapper, CloseBtn } from "./styles";
import SkusTable from "components/Table/Skus";
import ProductServices from "services/product.service";
import PriorityItemsServices from "services/priority-items.service";

import { IPriorityItem, IPriorityItemCreate } from "models/PriorityItem";
import Modal from "components/Modal";
import Text from "components/Text";
import { Row } from "components/Layout/Grid";
import { Button } from "components/Button";
import AddIcon from "img/add-icon-white.svg";
import Input from "components/Input";
import "./styles.scss";
import closeBtnImg from "img/cd-close-btn.svg";

import { ITableModel } from "components/Table/interface";
import { SearchArea } from "../../searchArea";

interface Product {
  code: string;
  name: string;
  substance: string;
  brand: string;
  category: string;
  selected?: boolean;
  id?: string;
}

interface IListProductsModal {
  setModalResponse: Dispatch<SetStateAction<boolean>>;
  notFound: boolean;
  codeProduct: string;
  substanceProduct: string;
  descriptionProduct: string;
  currentPageProduct: number;
  manufacturerProduct: string;
  setNotFound: Dispatch<SetStateAction<boolean>>;
  setCodeProduct: Dispatch<SetStateAction<string>>;
  setSubstanceProduct: Dispatch<SetStateAction<string>>;
  setDescriptionProduct: Dispatch<SetStateAction<string>>;
  setCurrentPageProduct: Dispatch<SetStateAction<number>>;
  setManufacturerProduct: Dispatch<SetStateAction<string>>;
  onAddSelectedItems: (items: IPriorityItemCreate[]) => void;
}

const priorityItemsService = new PriorityItemsServices();
const productService = new ProductServices();

const ListProductsModal = ({
  setModalResponse,
  notFound,
  setNotFound,
  codeProduct,
  setCodeProduct,
  substanceProduct,
  descriptionProduct,
  currentPageProduct,
  setSubstanceProduct,
  manufacturerProduct,
  setCurrentPageProduct,
  setDescriptionProduct,
  setManufacturerProduct,
  onAddSelectedItems,
}: IListProductsModal) => {
  const [products, setProducts] = useState<Product[]>([]);
  const [priorityCodes, setPriorityCodes] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(false);
  const [selectAll, setSelectAll] = useState(false);
  const [currentPage, setCurrentPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [hasSearched, setHasSearched] = useState(false);
  const pageSize = 10;

  const fetchPriorityCodes = useCallback(async () => {
    try {
      const metaResponse = await priorityItemsService.GetPriorityItems(
        0,
        1,
        ""
      );
      const totalPriorityItems = metaResponse.total || 0;
      let allPriorityData: IPriorityItem[] = [];
      if (totalPriorityItems > 0) {
        const response = await priorityItemsService.GetPriorityItems(
          0,
          totalPriorityItems,
          ""
        );
        allPriorityData = response.data || [];
      }
      const codes = new Set(
        allPriorityData.map((item: IPriorityItem) => item.supplierProductCode)
      );
      setPriorityCodes(codes);
    } catch (error) {
      console.error(
        "Erro ao buscar todos os códigos de produtos prioritários:",
        error
      );
      setPriorityCodes(new Set());
    }
  }, []);

  const fetchProducts = useCallback(async () => {
    setLoading(true);
    setSelectAll(false);
    try {
      await fetchPriorityCodes();

      const response = await productService.getProducts(
        manufacturerProduct,
        substanceProduct,
        descriptionProduct,
        codeProduct,
        currentPage,
        pageSize
      );

      const mappedProducts: Product[] = response.data.map((product: any) => ({
        code: product.code,
        name: product.name || "-",
        substance: product.substance || "-",
        brand: product.productBrand || "-",
        category: product.category || "-",
        selected: false,
        id: product.id,
      }));

      setProducts(mappedProducts);
      setNotFound(mappedProducts.length === 0 && hasSearched);
      setTotalPages(response.totalPages || 0);
    } catch (error) {
      console.error("Erro ao buscar produtos:", error);
      setProducts([]);
      setTotalPages(0);
      setNotFound(true);
    } finally {
      setLoading(false);
    }
  }, [
    manufacturerProduct,
    substanceProduct,
    descriptionProduct,
    codeProduct,
    currentPage,
    setNotFound,
    fetchPriorityCodes,
    hasSearched,
  ]);

  useEffect(() => {
    fetchPriorityCodes();
  }, [fetchPriorityCodes]);

  useEffect(() => {
    if (hasSearched) {
      fetchProducts();
    }
  }, [currentPage, hasSearched, fetchProducts]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Enter") {
        const target = e.target as HTMLElement;
        const isInput =
          target.tagName === "INPUT" || target.tagName === "TEXTAREA";
        if (isInput) {
          e.preventDefault();
          setCurrentPage(0);
          setHasSearched(true);
        }
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [codeProduct, substanceProduct, descriptionProduct, manufacturerProduct]);

  const handleActionCloseBtn = () => setModalResponse(false);

  const handleChangeSelectAllItems = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const checked = e.target.checked;
    setSelectAll(checked);
    setProducts((prevProducts) =>
      prevProducts.map((product) =>
        priorityCodes.has(product.code)
          ? product
          : { ...product, selected: checked }
      )
    );
  };

  const handleChangeSelectItems = (item: Product) => {
    if (!priorityCodes.has(item.code)) {
      setProducts((prevProducts) => {
        const newProducts = prevProducts.map((product) =>
          product.id === item.id
            ? { ...product, selected: !product.selected }
            : product
        );
        const currentSelectedCount = newProducts.filter(
          (p) => p.selected && !priorityCodes.has(p.code)
        ).length;
        const availableToSelectCount = newProducts.filter(
          (p) => !priorityCodes.has(p.code)
        ).length;
        setSelectAll(
          availableToSelectCount > 0 &&
            currentSelectedCount === availableToSelectCount
        );
        return newProducts;
      });
    }
  };

  const handleAddSelectedItems = () => {
    const selectedProductsToAdd = products.filter(
      (p) => p.selected && !priorityCodes.has(p.code)
    );

    if (selectedProductsToAdd.length > 0) {
      const itemsToCreate: IPriorityItemCreate[] = selectedProductsToAdd.map(
        (product) => {
          return {
            supplierProductCode: product.code,
            ean: product.code,
            supplierProductDescription: product.name,
            molecule: product.substance,
            supplier: product.brand,
            classification: product.category,
            selected: false,
            flagMolecule: product.substance,
            createdDate: moment().toISOString(),
            updatedDate: moment().toISOString(),
            deleted: false,
          };
        }
      );
      onAddSelectedItems(itemsToCreate);
      handleActionCloseBtn();
    }
  };

  const productsModelTable: Array<ITableModel> = [
    {
      label: "",
      key: "selected",
      noFilter: true,
      size: 60,
      maxSize: 60,
      Component: (item: Product) => (
        <Input
          type="checkbox"
          name={`select-${item.id || item.code}`}
          checked={!!item.selected}
          onChange={() => handleChangeSelectItems(item)}
          className="custom-checkbox"
          disabled={priorityCodes.has(item.code)}
        />
      ),
    },
    { label: "Código", key: "code", size: 150, maxSize: 150, noFilter: true },
    {
      label: "Descrição",
      key: "name",
      size: 180,
      maxSize: 330,
      noFilter: true,
    },
    {
      label: "Substância",
      key: "substance",
      size: 180,
      maxSize: 330,
      noFilter: true,
    },
    { label: "Marca", key: "brand", size: 150, maxSize: 205, noFilter: true },
    {
      label: "Categoria",
      key: "category",
      size: 150,
      maxSize: 205,
      noFilter: true,
    },
  ];

  const allFieldsEmpty =
    !codeProduct.trim() &&
    !substanceProduct.trim() &&
    !descriptionProduct.trim() &&
    !manufacturerProduct.trim();

  const shouldShowInitialEmptyState =
    !hasSearched && products.length === 0 && !loading;
  const shouldShowNotFoundState =
    hasSearched && products.length === 0 && notFound && !loading;

  const availableProductsToSelect = products.filter(
    (p) => !priorityCodes.has(p.code)
  ).length;
  const selectedProductsCount = products.filter(
    (p) => p.selected && !priorityCodes.has(p.code)
  ).length;
  const isAnyProductSelected = selectedProductsCount > 0;

  return (
    <ModalWrapper>
      <Modal
        title=""
        width="1200px"
        height="700px"
        minWidth="400px"
        borderRadius="8px"
        className="custom-modal-bg"
      >
        <Row className="flex-skuRow justify-between mb-0 mt-1">
          <Text size={24} className="color-primary font-bold">
            Seleção de SKU’s prioritários
          </Text>
          <CloseBtn
            src={closeBtnImg}
            alt="Fechar modal"
            className="CloseBtn"
            onClick={handleActionCloseBtn}
          />
        </Row>
        <Row className="flex-1 w-100 mb-2 mt-2">
          <SearchArea
            codeProduct={codeProduct}
            setCodeProduct={setCodeProduct}
            descriptionProduct={descriptionProduct}
            setDescriptionProduct={setDescriptionProduct}
            manufacturerProduct={manufacturerProduct}
            setManufacturerProduct={setManufacturerProduct}
            substanceProduct={substanceProduct}
            setSubstanceProduct={setSubstanceProduct}
            notFound={notFound}
            setNotFound={setNotFound}
            currentPageProduct={currentPageProduct}
            setCurrentPageProduct={setCurrentPageProduct}
            onSearch={() => {
              setCurrentPage(0);
              setHasSearched(true);
            }}
            onClearFields={() => {
              setCodeProduct("");
              setSubstanceProduct("");
              setDescriptionProduct("");
              setManufacturerProduct("");
              setCurrentPage(0);
              setProducts([]);
              setNotFound(false);
              setHasSearched(false);
              setSelectAll(false);
            }}
          />
        </Row>
        <Row className="ml-2 mt-1 items-center">
          <Row className="items-center">
            <Input
              type="checkbox"
              id="select-all-modal-products"
              name="selectAll"
              checked={selectAll}
              onChange={handleChangeSelectAllItems}
              className="self-start"
              disabled={availableProductsToSelect === 0 || loading}
            />
            <label
              htmlFor="select-all-modal-products"
              className="ml-1 mr-4 flex items-start"
              style={{ cursor: "pointer" }}
            >
              <Text>Selecionar Todos</Text>
            </label>
          </Row>
          {isAnyProductSelected && (
            <Button
              onClick={handleAddSelectedItems}
              className="bg-greenDarkViveo text-white text-sm font-bold px-2 py-1 rounded-md flex items-center gap-2"
              disabled={loading}
            >
              <img src={AddIcon} alt="Adicionar" className="w-4 h-4" />
              <Text className="button-text color-white font-bold ml-2">
                Adicionar selecionados ({selectedProductsCount})
              </Text>
            </Button>
          )}
        </Row>
        <TableWrapper className="table-wrapper">
          {loading ? (
            <div className="empty-state">Carregando produtos...</div>
          ) : shouldShowInitialEmptyState ? (
            <div className="empty-state">
              Utilize os campos de busca para encontrar os produtos.
            </div>
          ) : shouldShowNotFoundState ? (
            <div className="empty-state">
              Nenhum produto encontrado para os critérios informados. Tente
              outros termos.
            </div>
          ) : products.length > 0 ? (
            <SkusTable
              data={products}
              model={productsModelTable}
              currentPage={currentPage}
              setCurrentPage={setCurrentPage}
              totalPages={totalPages}
              pageSize={pageSize}
              rowClassName={(record: Product) =>
                priorityCodes.has(record.code) ? "gray-row" : ""
              }
            />
          ) : (
            <div className="empty-state">
              {hasSearched
                ? "Nenhum produto encontrado."
                : "Use a busca para listar produtos."}
            </div>
          )}
        </TableWrapper>
      </Modal>
    </ModalWrapper>
  );
};

export default ListProductsModal;
